import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import { getTypeColor, getStatusColor } from '../utils/colors';
import { formatInvestmentVolume, formatDate, formatJobs } from '../utils/formatters';
import { fetchClusterAnnouncements } from '../utils/api';
import { FeedbackDialog } from './FeedbackDialog';
import type { Announcement } from '../types';
import { stateLabel, statusLabel, typeLabel, useT } from '../i18n';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Portaled to document.body. Unlike the widget's inline content (capped at
     <= 9 so it scrolls under the host site's nav), this is an explicit modal
     dialog with a full-viewport backdrop: it must cover everything, including
     the host nav. We don't know the host nav's z-index (docs say >= 10, but
     real WP navs run to 9999), so sit at the top of the stack. */
  z-index: 2147483646;

  /* On phones, give the near-full-width card a small uniform margin from the
     screen edges instead of leaving it floating mid-screen. */
  @media (max-width: 600px) {
    padding: ${theme.spacing.md};
    /* Anchor toward the top so a short modal doesn't hover in the middle and
       a tall one has room to grow downward. */
    align-items: flex-start;
  }
`;

const ModalCard = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadow.lg};
  max-width: 640px;
  width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  font-family: ${theme.fontFamily};
  position: relative;

  /* Fill the (padded) viewport on phones rather than rendering as a small
     scaled-down desktop card. */
  @media (max-width: 600px) {
    width: 100%;
    max-width: none;
    max-height: 92vh;
    margin-top: ${theme.spacing.sm};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: none;
  border: none;
  font-size: ${theme.fontSize.xl};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  line-height: 1;
  border-radius: ${theme.borderRadius.sm};
  &:hover { background: rgba(0, 0, 0, 0.04); color: ${theme.colors.text.primary}; }
`;

const Content = styled.div`
  padding: ${theme.spacing.lg};
  overflow-y: auto;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${theme.colors.border}; border-radius: 3px; }

  /* Tighter padding on phones so the content isn't boxed in by the desktop
     24px inset. */
  @media (max-width: 600px) {
    padding: ${theme.spacing.md};
  }
`;

const Company = styled.h2`
  margin: 0 0 ${theme.spacing.xs} 0;
  font-size: ${theme.fontSize.xl};
  font-weight: ${theme.fontWeight.bold};
  color: ${theme.colors.text.primary};
  padding-right: ${theme.spacing.xl};

  /* The 30px desktop title is oversized on a phone; step it down. */
  @media (max-width: 600px) {
    font-size: ${theme.fontSize.lg};
  }
`;

const ProjectName = styled.p`
  margin: 0 0 ${theme.spacing.md} 0;
  font-size: ${theme.fontSize.md};
  color: ${theme.colors.text.secondary};
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

const TypeBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.medium};
  background: ${props => props.color}20;
  color: ${props => props.color};
`;

// Mirrors the InvestmentTable StatusBadge: soft pastel pill matching the
// website screenshot, sans serif, dark text.
const StatusBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.3em 0.8em;
  border-radius: 999px;
  font-family: var(--sans-stack);
  font-size: ${scaled(12)};
  font-weight: ${theme.fontWeight.normal};
  background: color-mix(in srgb, ${props => props.color} 35%, #ffffff);
  color: var(--color-text-default);
`;

const MetaText = styled.span`
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const Locations = styled.p`
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.border};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const SectionTitle = styled.h3`
  margin: 0 0 ${theme.spacing.md} 0;
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TimelineContainer = styled.div`
  position: relative;
  padding-left: 20px;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 5px;
  top: 6px;
  bottom: 6px;
  width: 2px;
  background: ${theme.colors.border};
`;

const TimelineEntry = styled.div`
  position: relative;
  margin-bottom: ${theme.spacing.md};
  &:last-child { margin-bottom: 0; }
`;

const TimelineDot = styled.div<{ filled: boolean }>`
  position: absolute;
  left: -20px;
  top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.primary};
  background: ${props => props.filled ? theme.colors.primary : theme.colors.surface};
`;

const EntryHeader = styled.button`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.sm};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  width: 100%;
  font-family: ${theme.fontFamily};

  &:hover { opacity: 0.7; }
`;

const EntryDate = styled.span`
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.primary};
  white-space: nowrap;
`;

const EntryHeadline = styled.span`
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.primary};
  font-weight: ${theme.fontWeight.medium};
`;

const SourceRow = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.secondary};
  margin-top: ${theme.spacing.xs};
`;

const SourceLink = styled.a`
  color: #2563EB;
  text-decoration: none;
  word-break: break-all;
  &:hover { text-decoration: underline; }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

// Muted feedback affordance under the timeline: quiet enough not to compete
// with the content, discoverable for anyone who spotted an error.
const FeedbackRow = styled.div`
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
`;

const FeedbackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  color: #2563EB;
  cursor: pointer;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

interface AnnouncementModalProps {
  announcement: Announcement;
  onClose: () => void;
  // When the opener already has the cluster's announcements (e.g. the map
  // bubble click fetches them to build the header), pass them here to skip
  // the modal's own cluster fetch.
  preloadedEntries?: Announcement[];
}

export function AnnouncementModal({ announcement: a, onClose, preloadedEntries }: AnnouncementModalProps) {
  const m = useT();
  const [clusterAnnouncements, setClusterAnnouncements] = useState<Announcement[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // While the feedback dialog is stacked on top, Escape belongs to it —
      // it has its own handler; closing both at once would be jarring.
      if (e.key === 'Escape' && !showFeedback) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, showFeedback]);

  useEffect(() => {
    if (preloadedEntries) {
      setClusterAnnouncements(preloadedEntries.length > 0 ? preloadedEntries : [a]);
      return;
    }
    if (a.cluster_id == null) {
      setClusterAnnouncements([a]);
      return;
    }
    setLoading(true);
    fetchClusterAnnouncements(a.cluster_id)
      .then(data => setClusterAnnouncements(data.length > 0 ? data : [a]))
      .catch(() => setClusterAnnouncements([a]))
      .finally(() => setLoading(false));
  }, [a, preloadedEntries]);

  const toggleExpanded = useCallback((id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const locationStr = a.locations.length > 0
    ? a.locations.map(l => `${l.city_name}${l.federal_state ? ` (${stateLabel(l.federal_state)})` : ''}`).join(', ')
    : null;

  const entries = clusterAnnouncements || [];
  const showTimeline = !loading && entries.length > 0;

  // The header shows the project's FIRST tracked date, matching the table's
  // "Erste Meldung" column and the time-series bucket. Taken as the minimum
  // over the loaded cluster history (which is ordered date-desc but NULLS LAST,
  // so the final entry isn't reliably the oldest); the fallback covers the map
  // path, where `a` comes from a MapPoint and carries no dates of its own until
  // the history has loaded.
  const firstTrackedDate = entries.reduce<string | null>(
    (min, e) => (e.announced_event_date && (!min || e.announced_event_date < min) ? e.announced_event_date : min),
    null,
  ) ?? a.first_announced_date ?? a.announced_event_date;

  // The project's job figure. Taken over the loaded cluster history, not from
  // `a` alone: entries come back date-desc, so the first one carrying a figure
  // is the most recently curated number, and on the map path `a` has no jobs at
  // all (announcementFromPoint hardcodes null). Falls back to `a` for the
  // render before the history has loaded.
  const jobsLabel = formatJobs(
    entries.find(e => e.jobs_created != null && e.jobs_created > 0)?.jobs_created
      ?? a.project_jobs_created
      ?? a.jobs_created,
  );

  return createPortal(
    <>
    <Backdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <Content>
          <Company>{a.investing_company || '-'}</Company>
          {a.project_name && <ProjectName>{a.project_name}</ProjectName>}

          <MetaRow>
            {a.type_of_investment && (
              <TypeBadge color={getTypeColor(a.type_of_investment)}>
                {typeLabel(a.type_of_investment)}
              </TypeBadge>
            )}
            {a.investment_status && (
              <StatusBadge color={getStatusColor(a.investment_status)}>
                {statusLabel(a.investment_status)}
              </StatusBadge>
            )}
            <MetaText>{formatInvestmentVolume(a.investment_volume_value_eur)}</MetaText>
            {jobsLabel && <MetaText>{jobsLabel}</MetaText>}
            <MetaText>{formatDate(firstTrackedDate)}</MetaText>
          </MetaRow>

          {locationStr && <Locations>{locationStr}</Locations>}

          <Divider />

          {loading && <LoadingText>{m.modal.loadingHistory}</LoadingText>}

          {showTimeline && (
            <>
              <SectionTitle>{m.modal.history(entries.length)}</SectionTitle>
              <TimelineContainer>
                {entries.length > 1 && <TimelineLine />}
                {entries.map(entry => {
                  const isExpanded = expandedIds.has(entry.id);

                  return (
                    <TimelineEntry key={entry.id}>
                      <TimelineDot filled={isExpanded} />
                      <EntryHeader onClick={() => toggleExpanded(entry.id)}>
                        <EntryDate>{formatDate(entry.announced_event_date)}</EntryDate>
                        <EntryHeadline>{entry.headline || (entry.type_of_investment ? typeLabel(entry.type_of_investment) : '-')}</EntryHeadline>
                      </EntryHeader>
                      {isExpanded && (
                        <SourceRow>
                          {m.modal.source}&nbsp;
                          {entry.source ? (
                            <SourceLink href={entry.source} target="_blank" rel="noopener noreferrer">
                              {entry.source}
                            </SourceLink>
                          ) : (
                            'S&P Global'
                          )}
                        </SourceRow>
                      )}
                    </TimelineEntry>
                  );
                })}
              </TimelineContainer>
            </>
          )}

          <FeedbackRow>
            {m.modal.errorInEntry}{' '}
            <FeedbackButton onClick={() => setShowFeedback(true)}>
              {m.modal.suggestCorrection}
            </FeedbackButton>
          </FeedbackRow>
        </Content>
      </ModalCard>
    </Backdrop>

    {/* Sibling of the Backdrop, NOT a child: the dialog portals to
        document.body, but React events bubble along the React tree — inside
        the Backdrop its overlay clicks would close this modal too. */}
    {showFeedback && (
      <FeedbackDialog
        mode="correction"
        announcement={a}
        onClose={() => setShowFeedback(false)}
      />
    )}
    </>,
    document.body,
  );
}
